import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import { getPreferredFormattedTimeMs } from "../../../utils/preferences";
import { numericalString } from "../../../types";

// Enforces a minimum range of 300ms before showing full red/yellow to stop misleading ranges
function getHeatmapColourMap(
  value: number,
  bound1: number,
  bound2: number,
): string {
  const MIN_RANGE_FOR_FULL_COLOUR = 300; // 300ms minimum range

  if (bound1 === bound2) return "#ffffff";

  if (bound1 < bound2) {
    value = Math.min(bound2, Math.max(bound1, value));
  } else {
    value = Math.max(bound2, Math.min(bound1, value));
  }

  const actualRange = Math.abs(bound2 - bound1);
  const normalized = (value - bound1) / (bound2 - bound1);

  const intensityFactor = Math.min(1, actualRange / MIN_RANGE_FOR_FULL_COLOUR);

  const colours = [
    { r: 34, g: 197, b: 94 },
    { r: 234, g: 179, b: 8 },
    { r: 239, g: 68, b: 68 },
  ];

  const neutral = { r: 234, g: 179, b: 8 };

  let targetR, targetG, targetB;
  if (normalized <= 0.5) {
    const factor = normalized * 2;
    targetR = colours[0].r + (colours[1].r - colours[0].r) * factor;
    targetG = colours[0].g + (colours[1].g - colours[0].g) * factor;
    targetB = colours[0].b + (colours[1].b - colours[0].b) * factor;
  } else {
    const factor = (normalized - 0.5) * 2;
    targetR = colours[1].r + (colours[2].r - colours[1].r) * factor;
    targetG = colours[1].g + (colours[2].g - colours[1].g) * factor;
    targetB = colours[1].b + (colours[2].b - colours[1].b) * factor;
  }

  const r = Math.round(neutral.r + (targetR - neutral.r) * intensityFactor);
  const g = Math.round(neutral.g + (targetG - neutral.g) * intensityFactor);
  const b = Math.round(neutral.b + (targetB - neutral.b) * intensityFactor);

  return `rgb(${r}, ${g}, ${b})`;
}

export default function LoopHeatMap() {
  const timingLoops = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.track?.info?.timingLoops,
    isEqual,
  );

  const sessionStatus = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.status,
    isEqual,
  );

  const participantDetails = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.standings?.overall?.participantDetails,
    isEqual,
  );

  const timingAnalysisLaps = useSelector(
    (state: RootState) => state.aks.v2.timing?.analysis?.laps,
    isEqual,
  );

  const standings = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.standings?.overall?.active,
    isEqual,
  );

  const entry = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.entry,
    isEqual,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const flags = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.flags,
    isEqual,
  );

  const cautionPeriods = useMemo(() => {
    if (!flags) return [];

    type FlagEvent = {
      time: number;
      type: "RED" | "FULL_YELLOW" | "GREEN" | "SAFETY_CAR";
    };

    const periods: { start: number; end: number }[] = [];

    const sortedFlags = Object.values(flags)
      .flatMap((flag): FlagEvent[] => {
        if (!flag || typeof flag.time !== "number") return [];
        if (
          flag.type !== "RED" &&
          flag.type !== "FULL_YELLOW" &&
          flag.type !== "GREEN" &&
          flag.type !== "SAFETY_CAR"
        ) {
          return [];
        }
        if (flag.type === "GREEN" && flag.sector !== undefined) return [];
        return [{ time: flag.time, type: flag.type }];
      })
      .sort((a, b) => a.time - b.time);

    let currentCaution: { time: number } | null = null;

    for (const flag of sortedFlags) {
      if (
        flag.type === "RED" ||
        flag.type === "FULL_YELLOW" ||
        flag.type === "SAFETY_CAR"
      ) {
        if (!currentCaution) {
          currentCaution = { time: flag.time };
        }
      } else if (flag.type === "GREEN") {
        if (currentCaution) {
          periods.push({
            start: currentCaution.time,
            end: flag.time,
          });
          currentCaution = null;
        }
      }
    }

    if (currentCaution) {
      periods.push({
        start: currentCaution.time,
        end: Infinity,
      });
    }

    return periods;
  }, [flags]);

  const loopIndices = useMemo(() => {
    if (!timingLoops) return [];
    const keys = Object.keys(timingLoops).map(Number);
    const max = Math.max(...keys.filter((n) => n !== 0));
    return keys
      .map((n) => (n === 0 ? max + 1 : n))
      .sort((a, b) => a - b)
      .map(String);
  }, [timingLoops]);

  const loopDisplayNames = useMemo(() => {
    if (!timingLoops) return [];
    const max = Math.max(
      ...Object.keys(timingLoops)
        .map(Number)
        .filter((n) => n !== 0),
    );
    return loopIndices.map((idx) => {
      const key = +idx === max + 1 ? "0" : idx;
      return timingLoops[key as numericalString]?.name || `L${idx}`;
    });
  }, [loopIndices, timingLoops]);

  const loopBounds = useMemo(() => {
    const isDuringCaution = (dayTimeMs: number) => {
      return cautionPeriods.some(
        (period) => dayTimeMs >= period.start && dayTimeMs <= period.end,
      );
    };

    const loopTimes: Record<string, number[]> = {};

    loopIndices.forEach((loopIdx) => {
      loopTimes[loopIdx] = [];
    });

    // Calculate lap window: last 5 completed laps (e.g., currentLap=35 -> laps 29-34)
    // If currentLap is not available, include all laps in the dataset
    const currentLap = sessionStatus?.currentLap;
    const minLapNum = currentLap ? currentLap - 6 : -Infinity;

    Object.entries(timingAnalysisLaps || {}).forEach(
      ([participant, participantAnalysis]) => {
        const laps = participantAnalysis?.laps;
        if (!laps) return;

        // If currentLoop === 0 for this driver, exclude their most recent lap
        const currentLoop =
          participantDetails?.[participant as keyof typeof participantDetails]
            ?.currentLoop;
        const lapNums = Object.keys(laps).map((k) => parseInt(k, 10));
        const maxLapNum = lapNums.length > 0 ? Math.max(...lapNums) : -1;
        const excludeLatestLap = currentLoop === 0;

        Object.entries(laps).forEach(([lapNumStr, lap]) => {
          if (!lap?.isValid) return;

          const lapNum = parseInt(lapNumStr, 10);
          if (excludeLatestLap && lapNum === maxLapNum) return;

          // Only include laps within the window (last 5 completed laps)
          // e.g., if currentLap=35, include laps 29-34
          // If currentLap not available, include all laps
          if (currentLap && (lapNum < minLapNum || lapNum >= currentLap))
            return;

          const loopSectors = lap?.loopSectors;
          if (!loopSectors) return;

          let prevCumulativeTime = 0;
          loopIndices.forEach((loopIdx) => {
            const loopData = loopSectors[loopIdx as numericalString];
            const cumulativeTime = loopData?.time;
            if (cumulativeTime !== undefined) {
              const perLoopTime = cumulativeTime - prevCumulativeTime;
              const loopDayTime = loopData?.dayTime;
              if (
                perLoopTime > 0 &&
                (loopDayTime === undefined || !isDuringCaution(loopDayTime))
              ) {
                loopTimes[loopIdx].push(perLoopTime);
              }
              prevCumulativeTime = cumulativeTime;
            }
          });
        });
      },
    );

    // Have the max bound be Q3 of the IQR to stop super slow outliers from skewing the color map
    const bounds: Record<string, { min: number; max: number }> = {};
    loopIndices.forEach((loopIdx) => {
      const times = loopTimes[loopIdx].sort((a, b) => a - b);
      if (times.length === 0) {
        bounds[loopIdx] = { min: 0, max: 0 };
      } else {
        const min = times[0];
        const q3Index = Math.floor(times.length * 0.75);
        const max = times[q3Index] || times[times.length - 1];
        bounds[loopIdx] = { min, max };
      }
    });

    return bounds;
  }, [
    timingAnalysisLaps,
    loopIndices,
    cautionPeriods,
    participantDetails,
    sessionStatus,
  ]);

  const standingsArray = Object.keys(standings || {})
    .map(Number)
    .sort((a, b) => a - b)
    .map((key) => standings?.[`${key}` as `${number}`]);

  return (
    <div className={styles.container}>
      <div className={styles.leaderboardTable}>
        <table>
          <colgroup>
            <col className={styles.smallCell} />
            <col className={styles.fixedCell} />
            <col className={styles.fixedCell} />
            {loopIndices.map((_, idx) => (
              <col key={idx} className={styles.loopCell} />
            ))}
          </colgroup>
          <thead>
            <tr className={styles.titleRow}>
              <th colSpan={3 + loopDisplayNames.length}>Loop Heatmap</th>
            </tr>
            <tr>
              <th>P</th>
              <th>Drv</th>
              <th>Lap</th>
              {loopDisplayNames.map((name, idx) => (
                <th key={idx}>{name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {standingsArray.map((standing, index) => {
              if (!standing) return null;
              const participant = standing.participant;
              const participantData = participantDetails?.[participant];
              const driverInfoEntry = driverInfo?.[participant];
              const entryData = entry?.[participant];
              const driverName =
                entryData?.drivers?.["1"]?.shortName ||
                driverInfoEntry?.driverName ||
                participant;
              const currentLap = participantData?.currentLap;
              const currentLoop = participantData?.currentLoop;

              // Get both current lap and last lap loop sectors
              const currentLoopSectors = participantData?.loopSectors;
              const lastLapLoopSectors = participantData?.lastLap?.loopSectors;

              const rowStyle = {
                color: driverInfoEntry?.driverFontColour || "white",
                backgroundColor: driverInfoEntry?.driverTeamColour || "#333",
              };

              // Calculate per-loop times for current lap
              const currentPerLoopTimes: Record<string, number> = {};
              let prevCumulativeTime = 0;
              loopIndices.forEach((loopIdx) => {
                const cumulativeTime =
                  currentLoopSectors?.[loopIdx as numericalString]?.time;
                if (cumulativeTime !== undefined) {
                  currentPerLoopTimes[loopIdx] =
                    cumulativeTime - prevCumulativeTime;
                  prevCumulativeTime = cumulativeTime;
                }
              });

              // Calculate per-loop times for last lap
              const lastLapPerLoopTimes: Record<string, number> = {};
              prevCumulativeTime = 0;
              loopIndices.forEach((loopIdx) => {
                const cumulativeTime =
                  lastLapLoopSectors?.[loopIdx as numericalString]?.time;
                if (cumulativeTime !== undefined) {
                  lastLapPerLoopTimes[loopIdx] =
                    cumulativeTime - prevCumulativeTime;
                  prevCumulativeTime = cumulativeTime;
                }
              });

              // Merge: use current lap data if available, otherwise use last lap data
              // but only for loops that are 2+ ahead of the current loop position
              // Special case: when currentLoop is 0, show last lap in full
              const perLoopTimes: Record<string, number> = {};
              const currentLoopPosition = loopIndices.findIndex(
                (idx) => parseInt(idx, 10) === currentLoop,
              );
              loopIndices.forEach((loopIdx, loopPosition) => {
                if (currentLoop === 0) {
                  // At loop 0, show full last lap
                  if (lastLapPerLoopTimes[loopIdx] !== undefined) {
                    perLoopTimes[loopIdx] = lastLapPerLoopTimes[loopIdx];
                  }
                } else if (currentPerLoopTimes[loopIdx] !== undefined) {
                  perLoopTimes[loopIdx] = currentPerLoopTimes[loopIdx];
                } else if (
                  lastLapPerLoopTimes[loopIdx] !== undefined &&
                  currentLoopPosition !== -1 &&
                  loopPosition >= currentLoopPosition + 2
                ) {
                  perLoopTimes[loopIdx] = lastLapPerLoopTimes[loopIdx];
                }
              });

              return (
                <tr key={participant} style={rowStyle}>
                  <td>{index + 1}</td>
                  <td>{driverName}</td>
                  <td>{currentLap ?? "-"}</td>
                  {loopIndices.map((loopIdx) => {
                    const loopTime = perLoopTimes[loopIdx];
                    const bounds = loopBounds[loopIdx];
                    const hasTime = loopTime !== undefined && loopTime > 0;

                    // White background if no data
                    const cellStyle = hasTime
                      ? {
                          backgroundColor: getHeatmapColourMap(
                            loopTime,
                            bounds.min,
                            bounds.max,
                          ),
                          color: "#000",
                        }
                      : {
                          backgroundColor: "#fff",
                          color: "#000",
                        };

                    return (
                      <td key={loopIdx} style={cellStyle}>
                        {hasTime ? getPreferredFormattedTimeMs(loopTime) : "-"}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
